import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { useConversation } from "@11labs/react";
import { supabase } from "@/integrations/supabase/client";
import VoiceOrb from "@/components/VoiceOrb";
import { MessageSquare, X, Mic, MicOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";

const ELEVENLABS_AGENT_ID = "agent_0901kaqfn7q2f97a81g5g1faj46t";

export function GlobalRoofingCoPilot() {
  const [isExpanded, setIsExpanded] = useState(false);
  const [transcript, setTranscript] = useState<Array<{ role: string; text: string }>>([]);
  const [voiceStatus, setVoiceStatus] = useState<"idle" | "connecting" | "connected" | "speaking">("idle");
  const [pendingAction, setPendingAction] = useState<{ type: string; parameters: any } | null>(null);
  const { user, orgId } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const conversation = useConversation({
    clientTools: {
      routeCommand: async (parameters: { user_input: string }) => {
        console.log("🎯 routeCommand called: Processing command...");
        setTranscript((prev) => [...prev, { role: "user", text: parameters.user_input }]);

        try {
          if (!user || !orgId) {
            toast.error("You need to be logged in");
            return "Please log in to use this feature.";
          }

          // FIX: Force a session refresh before getting the token
          await supabase.auth.refreshSession();
          const { data: sessionData } = await supabase.auth.getSession();
          const token = sessionData?.session?.access_token;

          if (!token) {
            toast.error("Authentication failed: Missing token");
            return "Please log in again to use this feature.";
          }

          // Extract rich context from current route
          const path = window.location.pathname;
          const jobIdMatch = path.match(/\/jobs\/([a-f0-9-]+)/);
          const leadIdMatch = path.match(/\/leads\/([a-f0-9-]+)/);

          const richContext = {
            path,
            job_id: jobIdMatch ? jobIdMatch[1] : null,
            lead_id: leadIdMatch ? leadIdMatch[1] : null,
            org_id: orgId,
          };

          // ADD ENHANCED DEBUG LOGGING - Step 4: Confirm frontend is sending org_id
          console.log("📤 [GlobalRoofingCoPilot] Sending to voice-brain-together:", {
            user_input: parameters.user_input,
            org_id_value: richContext.org_id,
            org_id_type: typeof richContext.org_id,
            full_context: richContext,
          });

          const { data, error } = await supabase.functions.invoke("voice-brain-together", {
            body: {
              user_input: parameters.user_input,
              context: richContext,
            },
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          if (error) throw error;

          console.log("🧠 Brain response: Action identified.");

          if (data.action) {
            console.log("📌 Storing action for post-speech execution:", data.action);
            setPendingAction(data.action);
          }

          if (data.spoken_response) {
            setTranscript((prev) => [...prev, { role: "agent", text: data.spoken_response }]);
          }

          return data.spoken_response || "Done.";
        } catch (error: any) {
          console.error("❌ routeCommand failure:", error);
          const errorMsg = `I encountered an error: ${error.message}`;
          setTranscript((prev) => [...prev, { role: "agent", text: errorMsg }]);
          return errorMsg;
        }
      },
    },
    onConnect: () => {
      console.log("✅ Voice connected");
      setVoiceStatus("connected");
    },
    onDisconnect: () => {
      console.log("🔌 Voice disconnected");
      setVoiceStatus("idle");
      setIsExpanded(false);
    },
    onError: (error: any) => {
      console.error("❌ Voice error:", error);
      toast.error("Voice error");
    },
  });

  const { isSpeaking } = conversation;

  useEffect(() => {
    if (!pendingAction) return;

    console.log("🎬 Executing pending action:", pendingAction);
    const { type, parameters: actionParams } = pendingAction;

    // NAVIGATE doesn't need auth - execute immediately
    const targetPath = actionParams?.path ?? actionParams?.route;
    if (type === "navigate" && targetPath) {
      console.log("🛑 NAVIGATING TO (voice action):", targetPath, "with raw params:", actionParams);
      navigate(targetPath, { replace: true });
      setPendingAction(null);
      return;
    }

    // For all OTHER actions that need database access, require auth
    (async () => {
      try {
        await supabase.auth.refreshSession();
        const { data: sessionData } = await supabase.auth.getSession();
        
        // Fetch fresh org_id from database instead of relying on useAuth
        const userId = sessionData?.session?.user?.id;
        const token = sessionData?.session?.access_token;

        if (!userId || !token) {
          toast.error("Session expired. Please refresh.");
          setPendingAction(null);
          return;
        }

        const { data: profile } = await supabase
          .from("profiles")
          .select("org_id")
          .eq("id", userId)
          .single();

        const freshOrgId = profile?.org_id;

        if (!freshOrgId) {
          toast.error("Organization data missing. Please refresh.");
          setPendingAction(null);
          return;
        }

        console.log("✅ Auth verified. Executing action with org_id:", freshOrgId);

          switch (type) {
          case "navigate_to_tab":
            // Dispatch custom event for JobDetail to listen to
            const tabName = actionParams.tab;
            console.log("🎯 Dispatching tab change event:", tabName);
            window.dispatchEvent(new CustomEvent('bridgette-change-tab', { 
              detail: { tab: tabName } 
            }));
            toast.success(`Opening ${tabName} tab`);
            break;

          case "open_form":
            // Dispatch custom event for JobDetail to open specific forms
            const formType = actionParams.form_type;
            console.log("🎯 Dispatching open form event:", formType);
            window.dispatchEvent(new CustomEvent('bridgette-open-form', { 
              detail: { formType } 
            }));
            toast.success(`Opening ${formType.replace('_', ' ')} form`);
            break;

          case "open_job":
            const { data: jobLookup, error: lookupError } = await supabase.functions.invoke("get-job-by-name", {
              body: {
                search_term: actionParams.owner_name,
                org_id: freshOrgId,
              },
              headers: { Authorization: `Bearer ${token}` },
            });

            if (!lookupError && jobLookup?.job_id) {
              toast.success(`Opening ${jobLookup.owner_name}'s job`);
              setTimeout(() => {
                navigate(`/jobs/${jobLookup.job_id}`);
              }, 300);
            } else {
              console.error("Job Lookup Error:", lookupError);
              toast.error(`Could not find job for "${actionParams.owner_name}"`);
            }
            break;

          case "create_job":
            const { data: jobData, error: jobError } = await supabase.functions.invoke("bridgette-create-job", {
              body: { ...actionParams, org_id: freshOrgId },
              headers: { Authorization: `Bearer ${token}` },
            });
            if (!jobError && jobData) {
              toast.success("Job created!");
              setTimeout(() => {
                navigate(`/jobs/${jobData.job_id}`);
              }, 500);
            } else {
              console.error("Job Creation Error:", jobError);
              toast.error(`Failed to create job: ${jobError?.message || "Unknown error"}`);
            }
            break;

          case "create_lead":
            const { data: leadData, error: leadError } = await supabase.functions.invoke("bridgette-create-lead", {
              body: { ...actionParams, lead_source: "voice_assistant", org_id: freshOrgId },
              headers: { Authorization: `Bearer ${token}` },
            });
            if (!leadData || leadError) {
              console.error("Lead Creation Error:", leadError);
              toast.error(`Failed to create lead: ${leadError?.message || "Unknown error"}`);
            } else {
              toast.success("Lead created!");
              setTimeout(() => {
                navigate(`/leads/${leadData.lead_id}`);
              }, 500);
            }
            break;

          case "compose_email":
            if (actionParams.recipient) {
              toast.success(`Preparing email for ${actionParams.recipient}...`);
              navigate(
                `/email/compose?to=${actionParams.recipient}&subject=${actionParams.subject || ""}&body=${actionParams.body || ""}`,
              );
            } else {
              toast.error("Cannot compose email: recipient missing.");
              navigate(`/email/compose`);
            }
            break;

          case "create_task":
            const { data: taskData, error: taskError } = await supabase.functions.invoke("bridgette-create-task", {
              body: { ...actionParams, created_by: userId, org_id: freshOrgId },
              headers: { Authorization: `Bearer ${token}` },
            });

            if (!taskData || taskError) {
              toast.error(`Failed to create task: ${taskError?.message || "Unknown error"}`);
            } else {
              toast.success(`Task created: ${taskData.title}`);
              navigate(`/calendar`);
            }
            break;

          case "order_measurement":
            const { data: measurementData, error: measurementError } = await supabase.functions.invoke(
              "bridgette-get-measurements",
              {
                body: {
                  job_id: actionParams.job_id,
                  address: actionParams.address,
                },
                headers: { Authorization: `Bearer ${token}` },
              },
            );
            if (!measurementError && measurementData) {
              toast.success("Measurement ordered!");
              navigate(`/measurements`);
            } else {
              console.error("Measurement Error:", measurementError);
              toast.error(`Failed to order measurement: ${measurementError?.message || "Unknown error"}`);
            }
            break;

          case "schedule_appointment":
            const { data: appointmentData, error: appointmentError } = await supabase.functions.invoke(
              "bridgette-schedule-appointment",
              {
                body: {
                  job_id: actionParams.job_id,
                  lead_id: actionParams.lead_id,
                  appointment_type: actionParams.appointment_type,
                  scheduled_date: actionParams.scheduled_date,
                  duration_minutes: actionParams.duration_minutes || 60,
                  notes: actionParams.notes,
                },
                headers: { Authorization: `Bearer ${token}` },
              },
            );
            if (!appointmentError && appointmentData) {
              toast.success("Appointment scheduled!");
              navigate(`/calendar`);
            } else {
              console.error("Appointment Error:", appointmentError);
              toast.error(`Failed to schedule appointment: ${appointmentError?.message || "Unknown error"}`);
            }
            break;

          case "strategize":
            toast.info("Opening AI Strategy Dashboard...");
            navigate(`/ai-monitoring?context=${actionParams.query || "general strategy"}`);
            break;

          default:
            console.warn("Unknown action type:", type);
        }

        setPendingAction(null);
      } catch (error: any) {
        console.error("❌ Action execution error:", error);
        toast.error(`Failed to execute action: ${error.message}`);
        setPendingAction(null);
      }
    })();
  }, [pendingAction, navigate]);

  const startSession = async () => {
    setTranscript([]);
    setVoiceStatus("connecting");

    try {
      // Ensure we have microphone permissions before starting the agent
      await navigator.mediaDevices.getUserMedia({ audio: true });

      if (!user || !orgId) {
        setVoiceStatus("idle");
        toast.error("Missing user or organization context. Please refresh the page.");
        return;
      }

      // Use public ElevenLabs agent – no Supabase tokens passed to ElevenLabs
      await (conversation as any).startSession({
        agentId: ELEVENLABS_AGENT_ID,
      });
    } catch (error: any) {
      console.error("Failed to start Bridgette voice session:", error);
      setVoiceStatus("idle");
      toast.error(error.message || "Failed to start voice: check microphone access and ElevenLabs connection.");
    }
  };

  const endSession = async () => {
    try {
      console.log("🔌 Ending Bridgette voice session...");
      if (conversation && typeof conversation.endSession === 'function') {
        await conversation.endSession();
      }
    } catch (e) {
      console.error("Error ending ElevenLabs session:", e);
    } finally {
      // Force reset all voice-related state
      setVoiceStatus("idle");
      setIsExpanded(false);
      setTranscript([]);
      setPendingAction(null);
      console.log("✅ Voice session ended and panel closed");
    }
  };

  const status = isSpeaking ? "speaking" : voiceStatus;

  return (
    <>
      {/* FLOATING ORB (collapsed state) - ENCHANTED VERSION */}
      {!isExpanded && (
        <div className={cn("fixed z-[9999] animate-fade-in", isMobile ? "bottom-28 right-6" : "bottom-6 right-6")}>
          {/* Outer magical ring */}
          <div className="absolute inset-0 rounded-full bg-gradient-to-r from-primary via-primary-glow to-secondary animate-spin-slow opacity-30 blur-xl scale-150" />

          {/* Floating particles */}
          <div className="absolute inset-0 animate-pulse">
            {[...Array(8)].map((_, i) => (
              <div
                key={i}
                className="absolute w-1 h-1 bg-primary rounded-full animate-float"
                style={{
                  top: `${50 + 40 * Math.sin((i * Math.PI * 2) / 8)}%`,
                  left: `${50 + 40 * Math.cos((i * Math.PI * 2) / 8)}%`,
                  animationDelay: `${i * 0.2}s`,
                  animationDuration: `${3 + i * 0.3}s`,
                }}
              />
            ))}
          </div>

          {/* Main orb button */}
          <button
            onClick={() => setIsExpanded(true)}
            className={cn(
              "relative w-20 h-20 rounded-full",
              "bg-gradient-to-br from-primary via-primary-glow to-secondary",
              "shadow-[0_0_60px_rgba(16,185,129,0.6)]",
              "transition-all duration-500",
              "hover:scale-125 hover:shadow-[0_0_80px_rgba(16,185,129,0.8)]",
              "flex items-center justify-center",
              "group",
              "border-2 border-primary-glow/50",
              "animate-pulse-glow",
            )}
            aria-label="Open Bridgette AI Assistant"
          >
            {/* Inner glow */}
            <div className="absolute inset-2 rounded-full bg-gradient-to-br from-primary-glow/40 to-transparent animate-pulse" />

            {/* Icon */}
            <MessageSquare className="relative w-9 h-9 text-primary-foreground drop-shadow-[0_0_8px_rgba(255,255,255,0.8)] group-hover:scale-110 transition-transform duration-300" />

            {/* Shimmer effect */}
            <div className="absolute inset-0 rounded-full bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer" />
          </button>
        </div>
      )}

      {/* EXPANDED PANEL - ENCHANTED VERSION */}
      {isExpanded && (
        <div className={cn("fixed z-[9999]", isMobile ? "inset-0" : "bottom-6 right-6")}>
          {/* Magical background glow */}
          <div className="absolute -inset-8 bg-gradient-to-r from-primary/20 via-primary-glow/20 to-secondary/20 rounded-3xl blur-3xl animate-pulse-slow opacity-60" />

          <div
            className={cn(
              "relative",
              isMobile ? "w-full h-full rounded-none" : "w-[420px] h-[600px] rounded-3xl",
              "bg-gradient-to-b from-background/98 via-background/95 to-background/98",
              "backdrop-blur-2xl",
              "border-2 border-primary/30",
              "shadow-[0_0_100px_rgba(16,185,129,0.4)]",
              "flex flex-col overflow-hidden",
              "animate-scale-in",
            )}
          >
            {/* HEADER - ENCHANTED */}
            <div className="relative flex items-center justify-between p-5 border-b border-primary/20 bg-gradient-to-r from-primary/10 via-primary-glow/5 to-secondary/10 overflow-hidden">
              {/* Animated background shimmer */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary/10 to-transparent animate-shimmer" />

              <div className="relative flex items-center gap-3">
                <div className="relative w-12 h-12 rounded-full bg-gradient-to-br from-primary via-primary-glow to-secondary flex items-center justify-center shadow-[0_0_20px_rgba(16,185,129,0.5)] animate-pulse-glow">
                  {/* Inner sparkle */}
                  <div className="absolute inset-1 rounded-full bg-gradient-to-br from-white/20 to-transparent animate-pulse" />
                  <MessageSquare className="relative w-6 h-6 text-primary-foreground drop-shadow-[0_0_8px_rgba(255,255,255,0.8)]" />
                </div>
                <div>
                  <h3 className="font-bold text-foreground text-lg flex items-center gap-2">
                    Bridgette AI
                    <span className="text-xs px-2 py-0.5 rounded-full bg-primary/20 text-primary font-normal animate-pulse">
                      {status === "speaking" ? "✨ Speaking" : status === "connected" ? "👂 Listening" : ""}
                    </span>
                  </h3>
                  <p className="text-xs text-muted-foreground font-medium">
                    {status === "idle" && "✨ Ready to enchant"}
                    {status === "connecting" && "🔮 Connecting magic..."}
                    {status === "connected" && "🎤 I'm listening"}
                    {status === "speaking" && "💬 Speaking now"}
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={async () => {
                  // End voice session if active before closing
                  if (status !== "idle") {
                    await endSession();
                  } else {
                    setIsExpanded(false);
                  }
                }}
                className="relative hover:bg-destructive/10 hover:scale-110 transition-all duration-300"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            {/* VOICE ORB - ENCHANTED */}
            <div className="relative flex-1 flex items-center justify-center overflow-hidden">
              {/* Animated gradient background */}
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-primary/5 via-background to-primary/5 animate-pulse-slow" />

              {/* Floating particles in background */}
              <div className="absolute inset-0">
                {[...Array(20)].map((_, i) => (
                  <div
                    key={i}
                    className="absolute w-1 h-1 bg-primary/30 rounded-full animate-float-random"
                    style={{
                      left: `${Math.random() * 100}%`,
                      top: `${Math.random() * 100}%`,
                      animationDelay: `${Math.random() * 5}s`,
                      animationDuration: `${5 + Math.random() * 10}s`,
                    }}
                  />
                ))}
              </div>

              <div className="relative scale-75 animate-fade-in">
                <VoiceOrb
                  isActive={status === "connected" || status === "speaking"}
                  isSpeaking={isSpeaking || status === "speaking"}
                />
              </div>
            </div>

            {/* TRANSCRIPT */}
            {transcript.length > 0 && (
              <div className="px-4 py-3 bg-muted/20 border-t border-border/50 max-h-40 overflow-y-auto">
                <p className="text-xs font-semibold text-muted-foreground mb-2">Conversation:</p>
                <div className="space-y-2">
                  {transcript.map((msg, i) => (
                    <div key={i} className={cn("text-xs", msg.role === "agent" ? "text-primary" : "text-foreground")}>
                      <span className="font-semibold">{msg.role === "agent" ? "Bridgette: " : "You: "}</span>
                      {msg.text}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* CONTROLS */}
            <div className="p-4 border-t border-border/50 bg-background">
              {status === "idle" ? (
                <Button
                  onClick={startSession}
                  className="w-full bg-gradient-to-r from-primary to-secondary text-primary-foreground font-semibold hover:opacity-90"
                  size="lg"
                >
                  <Mic className="w-5 h-5 mr-2" />
                  Start Voice Session
                </Button>
              ) : (
                <Button onClick={endSession} variant="destructive" className="w-full font-semibold" size="lg">
                  <MicOff className="w-5 h-5 mr-2" />
                  End Session
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
